import React, { useEffect, useRef, useState } from 'react';
import { 
  FaPhone, FaEnvelope, FaGithub, FaLinkedin, FaDownload, 
  FaPaperPlane, FaMapMarkerAlt, FaCalendarAlt, FaClock,
  FaUser, FaFilePdf, FaCheckCircle, FaStar, FaHandshake,
  FaSun, FaMoon
} from 'react-icons/fa';
import { SiLeetcode, SiHackerrank, SiDevdotto } from 'react-icons/si';
import { TypeAnimation } from 'react-type-animation';

const Contact = () => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            entry.target.classList.add('animate-contact-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const contactInfo = {
    phone: '+91 6370774480',
    email: 'tehseennayeemkhan@gmail.com',
    github: 'https://github.com/tehseennayeemkhan',
    linkedin: 'https://linkedin.com/in/tehseennayeemkhan',
    leetcode: 'https://leetcode.com/tehseennayeemkhan',
    hackerrank: 'https://hackerrank.com/tehseennayeemkhan',
    location: 'Mysore, Karnataka, India',
    availability: 'Available for opportunities',
    responseTime: 'Within 24 hours'
  };

  const socialLinks = [
    { 
      icon: <FaGithub className="h-6 w-6" />, 
      label: 'GitHub', 
      href: contactInfo.github,
      color: 'from-gray-800 to-gray-900',
      darkColor: 'from-gray-700 to-gray-800',
      username: '@tehseennayeemkhan'
    },
    { 
      icon: <FaLinkedin className="h-6 w-6" />, 
      label: 'LinkedIn', 
      href: contactInfo.linkedin,
      color: 'from-blue-700 to-blue-800',
      darkColor: 'from-blue-600 to-blue-700',
      username: 'Tehseen Khan'
    },
    { 
      icon: <SiLeetcode className="h-6 w-6" />, 
      label: 'LeetCode', 
      href: contactInfo.leetcode,
      color: 'from-yellow-600 to-orange-600',
      darkColor: 'from-yellow-500 to-orange-500',
      username: 'LeetCode Profile'
    },
    { 
      icon: <SiHackerrank className="h-6 w-6" />, 
      label: 'HackerRank', 
      href: contactInfo.hackerrank,
      color: 'from-green-600 to-emerald-500',
      darkColor: 'from-green-500 to-emerald-400',
      username: 'Problem Solving'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 2000);
  };

  const handleDownloadCV = () => {
    const link = document.createElement('a');
    link.href = '/resume.pdf';
    link.download = 'Tehseen_Khan_DevOps_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section 
      id="contact" 
      ref={containerRef}
      className={`relative py-20 overflow-hidden transition-all duration-500 ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}
    >
      Theme Toggle Button
      <button
        onClick={toggleDarkMode}
        className="fixed top-8 right-8 group z-50"
        aria-label="Toggle theme"
      >
        <div className="relative w-12 h-12">
          <div className={`absolute inset-0 rounded-full ${
            darkMode 
              ? 'bg-gradient-to-r from-gray-800 to-gray-900' 
              : 'bg-gradient-to-r from-white to-gray-50'
          } shadow-lg transition-all duration-500`}></div>
          <div className="relative w-full h-full flex items-center justify-center">
            <FaSun className={`absolute w-6 h-6 text-yellow-500 transition-all duration-500 ${
              darkMode ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
            }`} />
            <FaMoon className={`absolute w-6 h-6 text-blue-400 transition-all duration-500 ${
              darkMode ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
            }`} />
          </div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600/0 to-cyan-500/0 group-hover:from-blue-600/10 group-hover:to-cyan-500/10 transition-all duration-300"></div>
        </div>
      </button>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-blue-500/5 to-cyan-500/5' 
            : 'bg-gradient-to-r from-blue-300/10 to-cyan-300/10'
        }`}></div>
        <div className={`absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-purple-500/5 to-pink-500/5' 
            : 'bg-gradient-to-r from-purple-300/10 to-pink-300/10'
        }`}></div>
        
        {/* Animated communication lines */}
        <div className={`absolute inset-0 ${
          darkMode ? 'opacity-5' : 'opacity-10'
        }`}>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-32 h-32 border-2 rounded-full animate-ping ${
                darkMode ? 'border-blue-500' : 'border-blue-400'
              }`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.5}s`
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full blur-xl opacity-30 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center transform hover:rotate-12 transition-transform duration-500 shadow-xl">
              <FaHandshake className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center animate-bounce">
              <span className="text-white text-sm">📞</span>
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className={darkMode ? 'text-gray-100' : 'text-gray-900'}>Let's </span>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent animate-gradient">
              Connect
            </span>
          </h2>
          
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border text-sm font-medium ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-900/50 to-cyan-900/50 border-blue-800/50 text-cyan-300' 
              : 'bg-gradient-to-r from-blue-100/80 to-cyan-100/80 border-blue-200/50 text-blue-600'
          }`}>
            <div className="w-2 h-2 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full animate-ping"></div>
            <TypeAnimation
              sequence={[
                'Open to New Opportunities',
                2000,
                'Available for Collaborations',
                2000,
                'Hiring Inquiries Welcome',
                2000,
                'Technical Discussions Open',
                2000,
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              className="font-semibold"
            />
          </div>
        </div>

        {/* Contact Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Contact Information */}
          <div className={`space-y-8 animate-slide-up`} style={{ animationDelay: '100ms' }}>
            <div className={`relative overflow-hidden rounded-2xl shadow-xl border ${
              darkMode 
                ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-gray-700' 
                : 'bg-gradient-to-br from-white to-gray-50 border-gray-100'
            }`}>
              {/* Header */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-600 via-cyan-500 to-purple-500"></div>
              
              <div className="p-8">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center">
                    <FaUser className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-2xl font-bold ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>Contact Information</h3>
                    <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Reach out through any channel
                    </p>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="space-y-6">
                  {/* Phone */}
                  <a 
                    href={`tel:${contactInfo.phone}`}
                    className={`group flex items-center gap-4 p-4 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 ${
                      darkMode 
                        ? 'bg-gradient-to-r from-blue-900/20 to-cyan-900/20' 
                        : 'bg-gradient-to-r from-blue-50/50 to-cyan-50/50'
                    }`}
                  >
                    <div className="relative">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                        <FaPhone className="h-6 w-6 text-white" />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg blur opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Phone Number</p>
                      <p className={`font-bold group-hover:text-blue-400 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-900'
                      }`}>
                        {contactInfo.phone}
                      </p>
                    </div>
                    <FaPaperPlane className={`group-hover:text-blue-500 transition-colors duration-300 transform group-hover:translate-x-2 ${
                      darkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                  </a>

                  {/* Email */}
                  <a 
                    href={`mailto:${contactInfo.email}`}
                    className={`group flex items-center gap-4 p-4 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 ${
                      darkMode 
                        ? 'bg-gradient-to-r from-green-900/20 to-emerald-900/20' 
                        : 'bg-gradient-to-r from-green-50/50 to-emerald-50/50'
                    }`}
                  >
                    <div className="relative">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                        <FaEnvelope className="h-6 w-6 text-white" />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg blur opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Email Address</p>
                      <p className={`font-bold group-hover:text-green-400 transition-colors duration-300 break-all ${
                        darkMode ? 'text-gray-300' : 'text-gray-900'
                      }`}>
                        {contactInfo.email}
                      </p>
                    </div>
                    <FaPaperPlane className={`group-hover:text-green-500 transition-colors duration-300 transform group-hover:translate-x-2 ${
                      darkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                  </a>

                  {/* Location */}
                  <div className={`group flex items-center gap-4 p-4 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 ${
                    darkMode 
                      ? 'bg-gradient-to-r from-purple-900/20 to-pink-900/20' 
                      : 'bg-gradient-to-r from-purple-50/50 to-pink-50/50'
                  }`}>
                    <div className="relative">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <FaMapMarkerAlt className="h-6 w-6 text-white" />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg blur opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Location</p>
                      <p className={`font-bold group-hover:text-purple-400 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-900'
                      }`}>
                        {contactInfo.location}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Availability */}
                <div className={`mt-8 pt-8 border-t ${
                  darkMode ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`text-center p-3 rounded-lg ${
                      darkMode 
                        ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30' 
                        : 'bg-gradient-to-r from-blue-50 to-cyan-50'
                    }`}>
                      <FaCalendarAlt className={`h-5 w-5 mx-auto mb-2 ${
                        darkMode ? 'text-blue-400' : 'text-blue-600'
                      }`} />
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Availability</p>
                      <p className={`font-bold ${
                        darkMode ? 'text-blue-300' : 'text-blue-700'
                      }`}>{contactInfo.availability}</p>
                    </div>
                    <div className={`text-center p-3 rounded-lg ${
                      darkMode 
                        ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30' 
                        : 'bg-gradient-to-r from-green-50 to-emerald-50'
                    }`}>
                      <FaClock className={`h-5 w-5 mx-auto mb-2 ${
                        darkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Response Time</p>
                      <p className={`font-bold ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>{contactInfo.responseTime}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className={`relative overflow-hidden rounded-2xl shadow-xl border ${
              darkMode 
                ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-gray-700' 
                : 'bg-gradient-to-br from-white to-gray-50 border-gray-100'
            }`}>
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-purple-600 via-pink-500 to-red-500"></div>
              
              <div className="p-8">
                <h3 className={`text-xl font-bold mb-6 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>Connect on Social</h3>
                <div className="grid grid-cols-2 gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group relative overflow-hidden p-4 rounded-xl border hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                        darkMode 
                          ? 'bg-gray-800/50 border-gray-700' 
                          : 'bg-gradient-to-br from-white to-gray-50 border-gray-100'
                      }`}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${
                        darkMode ? social.darkColor : social.color
                      } opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                      
                      <div className="relative z-10 flex flex-col items-center text-center">
                        <div className={`mb-3 p-3 rounded-lg bg-gradient-to-br ${
                          darkMode ? social.darkColor : social.color
                        } text-white transform group-hover:scale-110 transition-transform duration-500`}>
                          {social.icon}
                        </div>
                        <div>
                          <p className={`font-bold ${
                            darkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>{social.label}</p>
                          <p className={`text-xs mt-1 ${
                            darkMode ? 'text-gray-500' : 'text-gray-600'
                          }`}>{social.username}</p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form & Download Section */}
          <div className={`space-y-8 animate-slide-up`} style={{ animationDelay: '200ms' }}>
            {/* Contact Form */}
            <div className={`relative overflow-hidden rounded-2xl shadow-xl border ${
              darkMode 
                ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-gray-700' 
                : 'bg-gradient-to-br from-white to-gray-50 border-gray-100'
            }`}>
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-green-600 to-emerald-500"></div>
              
              <div className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                    <FaPaperPlane className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-2xl font-bold ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>Send a Message</h3>
                    <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Get in touch directly
                    </p>
                  </div>
                </div>

                {submitStatus === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <FaCheckCircle className="h-10 w-10 text-white" />
                    </div>
                    <h4 className={`text-2xl font-bold mb-3 ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>Message Sent!</h4>
                    <p className={`mb-6 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Thank you for reaching out. I'll get back to you within 24 hours.
                    </p>
                    <button
                      onClick={() => setSubmitStatus(null)}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Your Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 group-hover:shadow-md ${
                            darkMode 
                              ? 'bg-gray-800 border-gray-700 text-gray-100' 
                              : 'bg-gray-50 border-gray-300'
                          }`}
                          placeholder="John Doe"
                        />
                      </div>
                      
                      <div className="group">
                        <label className={`block text-sm font-medium mb-2 ${
                          darkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 group-hover:shadow-md ${
                            darkMode 
                              ? 'bg-gray-800 border-gray-700 text-gray-100' 
                              : 'bg-gray-50 border-gray-300'
                          }`}
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    
                    <div className="group">
                      <label className={`block text-sm font-medium mb-2 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 group-hover:shadow-md ${
                          darkMode 
                            ? 'bg-gray-800 border-gray-700 text-gray-100' 
                            : 'bg-gray-50 border-gray-300'
                        }`}
                        placeholder="Project Inquiry"
                      />
                    </div>
                    
                    <div className="group">
                      <label className={`block text-sm font-medium mb-2 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        className={`w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 group-hover:shadow-md resize-none ${
                          darkMode 
                            ? 'bg-gray-800 border-gray-700 text-gray-100' 
                            : 'bg-gray-50 border-gray-300'
                        }`}
                        placeholder="Tell me about your project or opportunity..."
                      ></textarea>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="group relative overflow-hidden w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <FaPaperPlane className="mr-2 group-hover:rotate-12 transition-transform duration-300" />
                            Send Message
                          </>
                        )}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Download Resume */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-cyan-500 to-purple-600 shadow-2xl text-white">
              {/* Animated background pattern */}
              <div className="absolute inset-0 opacity-10">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-16 h-16 border-2 border-white rounded-full animate-pulse"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${i * 0.7}s`
                    }}
                  ></div>
                ))}
              </div>
              
              <div className="relative z-10 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <FaFilePdf className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Download Resume</h3>
                    <p className="text-blue-100">Complete professional profile</p>
                  </div>
                </div>
                
                <p className="text-blue-100 mb-8 leading-relaxed">
                  Get my comprehensive resume including detailed experience, project case studies, 
                  technical skills matrix, and professional achievements.
                </p>
                
                <button
                  onClick={handleDownloadCV}
                  className="group relative overflow-hidden w-full py-4 bg-white text-blue-600 rounded-lg font-semibold hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                >
                  <span className="relative z-10 flex items-center">
                    <FaDownload className="mr-2 group-hover:animate-bounce" />
                    Download Resume (PDF)
                  </span>
                  <div className="absolute inset-0 bg-gray-100 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                </button>
                
                <div className="mt-8 bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                  <h4 className="font-bold mb-4 flex items-center gap-2">
                    <FaStar className="text-yellow-300" />
                    What's Included:
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      "Work Experience",
                      "Project Details",
                      "Technical Skills",
                      "Achievements",
                      "Certifications",
                      "Education",
                      "Contact Info",
                      "References"
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-sm text-blue-100">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className={`inline-flex items-center gap-6 px-8 py-6 rounded-2xl border backdrop-blur-sm ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-gray-700/30' 
              : 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-blue-200/30'
          }`}>
            <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full flex items-center justify-center">
              <FaHandshake className="h-7 w-7 text-white" />
            </div>
            <div className="text-left">
              <p className={`text-xl font-semibold ${
                darkMode ? 'text-gray-100' : 'text-gray-800'
              }`}>
                Ready to discuss your next project?
              </p>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Let's collaborate on building scalable, reliable cloud infrastructure.
              </p>
            </div>
            <a
              href={`mailto:${contactInfo.email}`}
              className="group/btn relative overflow-hidden px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300"
            >
              <span className="relative z-10 flex items-center gap-2">
                <FaPaperPlane className="group-hover/btn:rotate-12 transition-transform duration-300" />
                Start Conversation
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 transform translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500"></div>
            </a>
          </div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes contact-in {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animate-slide-up {
          animation: slide-up 0.6s ease-out forwards;
        }
        
        .animate-contact-in {
          animation: contact-in 0.6s ease-out forwards;
        }
        
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
        
        .animate-ping {
          animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        
        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
      `}</style>
    </section>
  );
};

export default Contact;